// src/pages/feasibility/PvSizingTornado.jsx
//
// How far each assumption swings the NPV of the base size, largest first.
// One line per assumption; its low and high shift are two thin bars on that
// line, drawn from the base NPV (the centre line) left for a loss and right
// for a gain. Side and sign carry the direction, so it never rests on colour.

import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { SCENARIO_LABELS, SENSITIVITY_GROUP_LABELS, fmtEur, fmtKwp, fmtSignedEur } from "./pvSizingModel";

// Diverging pair (blue gain, red loss), checked for colour-vision separation
// and contrast on the light and the dark surface.
const POLES = {
  light: { gain: "#3a76b8", loss: "#c4513a" },
  dark: { gain: "#4a8bd4", loss: "#d0644c" },
};
// The longest bar reaches this share of each half, leaving room for its value.
const REACH = 0.72;

export default function PvSizingTornado({ groups, base, isGreek }) {
  const { palette } = useTheme();
  const poles = POLES[palette.mode === "dark" ? "dark" : "light"];
  const tr = (el, en) => (isGreek ? el : en);
  const maxAbs = Math.max(0, ...groups.flatMap((g) => g.rows.map((r) => (Number.isFinite(r.change) ? Math.abs(r.change) : 0))));
  if (!groups.length || !(maxAbs > 0)) return null;
  const label = (key) => (SCENARIO_LABELS[key] || [key, key])[isGreek ? 0 : 1];

  const bar = (r) => {
    if (!Number.isFinite(r.change)) return null;
    const half = (Math.abs(r.change) / maxAbs) * REACH * 50;
    const gain = r.change >= 0;
    const text = fmtSignedEur(r.change, isGreek);
    return (
      <Box key={r.key} title={`${label(r.key)}: ${text}`} sx={{ position: "relative", height: 13 }}>
        <Box sx={{
          position: "absolute", top: 2, height: 9, borderRadius: gain ? "0 4px 4px 0" : "4px 0 0 4px",
          bgcolor: gain ? poles.gain : poles.loss,
          left: gain ? "50%" : `calc(50% - ${half}%)`, width: `max(${half}%, 2px)`,
        }} />
        <Typography component="span" sx={{
          position: "absolute", top: -2, fontSize: 11, lineHeight: "16px", color: "text.secondary", whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
          ...(gain ? { left: `calc(50% + ${half}% + 6px)` } : { right: `calc(50% + ${half}% + 6px)` }),
        }}>
          {text}
        </Typography>
      </Box>
    );
  };

  return (
    <Box data-testid="sensitivity-tornado" sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 2, rowGap: 0.5, mb: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {tr("Μεταβολή NPV στη βασική ισχύ", "NPV change at the base size")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {tr("έναντι της βάσης", "against the base")} {fmtEur(base?.npv_eur, isGreek)} ({fmtKwp(base?.k_kwp, isGreek)} kWp) · {tr("μεγαλύτερη επίδραση πρώτα", "largest effect first")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, ml: "auto" }}>
          {[["loss", tr("μείωση", "decrease")], ["gain", tr("αύξηση", "increase")]].map(([k, text]) => (
            <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: poles[k] }} />
              <Typography variant="caption" color="text.secondary">{text}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ position: "relative" }}>
        {/* The base NPV: the line every bar starts from. */}
        <Box aria-hidden sx={{
          position: "absolute", top: 0, bottom: 0, width: "1px", bgcolor: "divider",
          left: { xs: "calc(160px + (100% - 160px) / 2)", sm: "calc(220px + (100% - 220px) / 2)" },
        }} />
        {groups.map((g) => (
          <Box key={g.group} sx={{
            display: "grid", gridTemplateColumns: { xs: "160px minmax(0, 1fr)", sm: "220px minmax(0, 1fr)" },
            alignItems: "center", py: 0.4, borderBottom: "1px solid", borderColor: "divider",
            "&:last-of-type": { borderBottom: "none" },
          }}>
            <Typography variant="body2" sx={{ pr: 1, fontSize: 13 }}>
              {(SENSITIVITY_GROUP_LABELS[g.group] || [label(g.group), label(g.group)])[isGreek ? 0 : 1]}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {g.rows.map(bar)}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
